import React, { useEffect, useRef, useState } from "react"
import parse from "html-react-parser"
import Box from "@mui/material/Box"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import Typography from "@mui/material/Typography"
import { CircularProgress, DialogActions, Stack } from "@mui/material"
import Button from "@mui/material/Button"
import { getUri } from "../../utils/getFullUrl"

export const PreviewOpenedBox = ({
  nft,
  closeDialog,
  maxWidth = 900,
  videoSrc,
}) => {
  const videoRef = useRef(null)
  const [isVideoEnded, setIsVideoEnded] = useState(false)
  const [showText, setShowText] = useState(false)
  const [isVideoLoading, setIsVideoLoading] = useState(true)

  const nftVideoSrc = getUri("video", "mp4", nft.type, nft.name, nft.rarity)
  const nftImageSrc = getUri("img", "webp", nft.type, nft.name, nft.rarity)

  useEffect(() => {
    if (isVideoEnded) {
      const timer = setTimeout(() => setShowText(true), 150)
      return () => clearTimeout(timer)
    }

    setShowText(false)
  }, [isVideoEnded])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !videoSrc) return

    setIsVideoLoading(true)

    const handleCanPlay = async () => {
      setIsVideoLoading(false)
      try {
        await video.play()
      } catch (e) {
        // autoplay может быть заблокирован браузером
        setIsVideoLoading(false)
      }
    }

    const handleLoadedData = () => {
      setIsVideoLoading(false)
    }

    video.addEventListener("canplay", handleCanPlay)
    video.addEventListener("loadeddata", handleLoadedData)

    return () => {
      video.removeEventListener("canplay", handleCanPlay)
      video.removeEventListener("loadeddata", handleLoadedData)
    }
  }, [videoSrc])

  const text = isVideoEnded
    ? `Congratulations!<br />You received <span class="primary">${nft.rarity} ${nft.name} ${nft.type}</span> NFT.<br />This NFT has been added to your inventory.`
    : "Opening your box…"

  return (
    <Box
      sx={{
        maxWidth,
        background: "#121212",
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1rem",
          position: "relative",
          pr: 6,
          textAlign: "center",
        }}
      >
        Opening NFT Box
        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {!videoSrc ? (
          <Typography color="text.secondary">
            Video source not provided
          </Typography>
        ) : (
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: "500px",
                minWidth: {
                  xs: "auto",
                  lg: "500px",
                },
                aspectRatio: "1 / 1",
                borderRadius: "12px",
                margin: "0 auto",
                backgroundColor: "#000",
                position: "relative",
                overflow: "hidden",
                overflowX: "hidden",
                transition: "clip-path 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                clipPath: isVideoEnded
                  ? "inset(1% 12% 3% 11.5%)"
                  : "inset(0% 0% 0% 0%)",
              }}
            >
              {isVideoLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 1,
                    zIndex: 2,
                    backgroundColor: "rgba(0, 0, 0, 0.55)",
                  }}
                >
                  <CircularProgress color="inherit" />
                  <Typography sx={{ color: "#fff" }}>Loading</Typography>
                </Box>
              )}

              {!isVideoEnded && (
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  preload="metadata"
                  onEnded={() => setIsVideoEnded(true)}
                  onLoadedData={() => setIsVideoLoading(false)}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 12,
                    background: "#000",
                    objectFit: "cover",
                    opacity: isVideoLoading ? 0 : 1,
                    transition: "opacity 0.25s ease",
                  }}
                >
                  <source src={videoSrc} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}

              {isVideoEnded && (
                <video
                  autoPlay
                  muted
                  playsInline
                  preload="auto"
                  loop
                  poster={nftImageSrc}
                  style={{
                    width: "76%",
                    height: "auto",
                    marginLeft: "11.5%",
                    marginTop: "1%",
                    borderRadius: 12,
                    background: "#000",
                    objectFit: "cover",
                  }}
                >
                  <source src={nftVideoSrc} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </Box>

            <Typography
              sx={{
                maxWidth: "500px",
                whiteSpace: "pre-line",
                transition: "opacity 0.35s ease",
                textAlign: "center",
                pt: 2,
              }}
            >
              {isVideoEnded ? parse(text) : text}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          pb: 3,
          width: "100%",
          justifyContent: "center",
        }}
      >
        <Stack flexWrap="wrap" direction="horizontal" useFlexGap spacing={1}>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              closeDialog()
            }}
            disabled={!isVideoEnded}
            sx={{ fontFamily: "RUSBoycott, sans-serif" }}
          >
            Continue
          </Button>
        </Stack>
      </DialogActions>
    </Box>
  )
}
